(() => {
  if (customElements.get('predictive-search')) return;

  const normalizeSearch = query => window.DripnordSearch?.normalize(query) ?? query.trim();

  // These are supported Predictive Search API fields, not storefront q syntax.
  const PRODUCT_SEARCH_FIELDS = [
    'title', 'body', 'product_type', 'tag', 'variants.title',
    'variants.sku', 'variants.barcode', 'vendor'
  ];

  const escapeLegacyToken = token => token.replace(/([\\:()])/g, '\\$1');

  // Recover only the exact query shape generated by the previous theme. Manual
  // queries, including quotes, operators and wildcards, pass through unchanged.
  const recoverLegacyQuery = query => {
    if (!query.startsWith('(title:')) return null;
    const tokens = [];
    for (const group of query.split(' AND ')) {
      if (!group.startsWith('(title:') || !group.endsWith(')')) return null;
      const tokenEnd = group.indexOf('* OR body:');
      if (tokenEnd < 8) return null;
      const encodedToken = group.slice(7, tokenEnd);
      if (/\s/.test(encodedToken)) return null;
      const token = encodedToken.replace(/\\([\\:()])/g, '$1');
      const rebuiltGroup = `(${PRODUCT_SEARCH_FIELDS.map(field => `${field}:${escapeLegacyToken(token)}*`).join(' OR ')})`;
      if (rebuiltGroup !== group) return null;
      tokens.push(token);
    }
    return tokens.length ? tokens.join(' ') : null;
  };

  if (window.location.pathname.replace(/\/+$/, '').endsWith('/search')) {
    const url = new URL(window.location.href);
    const originalQuery = url.searchParams.get('q') || '';
    const recoveredQuery = recoverLegacyQuery(originalQuery);
    const normalizedQuery = normalizeSearch(recoveredQuery ?? originalQuery);
    if (normalizedQuery !== originalQuery) {
      url.searchParams.set('q', normalizedQuery);
      // A narrower brand query can have fewer pages (AMI page 5, for example).
      // Already canonical URLs never enter this branch, so their pages persist.
      url.searchParams.delete('page');
      // Direct/bookmarked searches use the same query as forms and autocomplete.
      // Reload actual Shopify results, retaining filters and sorting.
      window.location.replace(url.href);
    }
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.listeners = [];
      this.requestSequence = 0;
      this.timeout = null;
      this.abortController = null;
      this.isOpen = false;
      this.isComposing = false;
      this.initialized = false;
      this.pendingDismissal = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      if (!this.form || !this.input) return;

      this.initialized = true;
      this.resultsContainer = this.querySelector('.js-results-container');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector('.js-message-error');
      this.searchDrawer = this.closest('search-drawer');
      this.drawerDetails = this.searchDrawer?.querySelector('details');
      this.drawerSummary = this.searchDrawer?.querySelector('summary');
      this.searchDrawerContent = this.closest('.drawer__content--search');
      this.searchSuggestions = this.searchDrawerContent?.querySelector('.search-drawer__suggestions');
      this.initialDrawerHeight = this.searchDrawerContent?.style.height || '';
      this.initialSuggestionsDisplay = this.searchSuggestions?.style.display || '';

      this.listen(this.form, 'submit', event => {
        if (!this.input.value.trim()) event.preventDefault();
        if (!event.defaultPrevented) this.pendingDismissal = false;
        this.cancelPending();
      });
      this.listen(this.form, 'formdata', event => {
        // Use the same canonical query as autocomplete without editing the input.
        event.formData.set('q', normalizeSearch(this.input.value));
      });
      this.listen(this.input, 'input', event => this.handleInput(event));
      this.listen(this.input, 'compositionstart', () => {
        this.isComposing = true;
        this.close();
      });
      this.listen(this.input, 'compositionend', () => {
        this.isComposing = false;
        this.handleInput();
      });
      this.listen(this.btnClearText, 'click', () => this.close({ clear: true, focusInput: true }));
      this.listen(this, 'keydown', event => this.handleKeyboardNavigation(event));
      this.listen(this, 'focusout', event => {
        if (!this.contains(event.relatedTarget)) this.close();
      });
      this.listen(window, 'pagehide', () => {
        this.pendingDismissal = false;
        this.close();
      });
      this.listen(window, 'click', event => {
        if (!this.pendingDismissal || event.defaultPrevented || event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest?.('a[href]');
        if (!link || link.hasAttribute('download') ||
          (link.target && !['_self', '_parent', '_top'].includes(link.target))) return;
        const destination = new URL(link.href, window.location.href);
        if (['http:', 'https:'].includes(destination.protocol)) this.pendingDismissal = false;
      });

      // Closing the containing drawer also invalidates in-flight suggestions.
      // Observe the existing drawer state; it still owns focus and scroll locks.
      if (this.drawerDetails) {
        this.listen(this.searchDrawer, 'click', event => {
          const control = event.target.closest('summary, .js-btn-close-drawer');
          if (control === this.drawerSummary || (control && control.closest('.js-drawer') === this.searchDrawerContent)) {
            this.rememberDismissal();
          }
        });
        this.listen(this.searchDrawer, 'keydown', event => {
          if (event.key === 'Escape') this.rememberDismissal();
        });
        this.drawerObserver = new MutationObserver(() => {
          if (this.drawerIsOpen()) {
            this.pendingDismissal = false;
          } else {
            this.close();
            this.restoreDismissalFocus();
          }
        });
        this.drawerObserver.observe(this.drawerDetails, {
          attributes: true, attributeFilter: ['open', 'class']
        });
      }
      this.updateClearButton();
    }

    disconnectedCallback() {
      this.close();
      this.listeners.forEach(([target, type, listener]) => target.removeEventListener(type, listener));
      this.listeners = [];
      this.drawerObserver?.disconnect();
      this.drawerObserver = null;
      this.initialized = false;
      this.isComposing = false;
      this.pendingDismissal = false;
    }

    listen(target, type, listener) {
      if (!target) return;
      target.addEventListener(type, listener);
      this.listeners.push([target, type, listener]);
    }

    drawerIsOpen() {
      return !this.drawerDetails || (
        this.drawerDetails.hasAttribute('open') &&
        this.drawerDetails.classList.contains('menu-opening')
      );
    }

    rememberDismissal() {
      if (!this.drawerIsOpen()) {
        this.pendingDismissal = true;
        this.restoreDismissalFocus();
      }
    }

    restoreDismissalFocus() {
      if (!this.pendingDismissal || this.drawerDetails?.hasAttribute('open')) return;
      this.pendingDismissal = false;
      const active = document.activeElement;
      // The old drawer controller waits for a visibility transition that its
      // CSS never starts. Restore after details really closes, but only for a
      // dismissal, and never take focus from a new page control or another drawer.
      if (this.isConnected && this.drawerSummary?.getClientRects().length &&
        (active === document.body || this.searchDrawer.contains(active))) {
        if (active !== this.drawerSummary) this.drawerSummary.focus({ preventScroll: true });
      }
    }

    updateClearButton() {
      if (this.btnClearText) this.btnClearText.disabled = !this.input?.value;
    }

    cancelPending() {
      clearTimeout(this.timeout);
      this.timeout = null;
      this.requestSequence += 1;
      this.abortController?.abort();
      this.abortController = null;
      this.resultsContainer?.removeAttribute('aria-busy');
    }

    handleInput(event) {
      const searchTerm = this.input.value.trim();
      // Keep an existing results drawer stable while the next query debounces;
      // popular searches should not flash between every character typed.
      this.close({ restoreDrawer: !searchTerm });
      this.updateClearButton();
      if (!this.resultsContainer || !searchTerm || this.isComposing || event?.isComposing) return;
      this.timeout = setTimeout(() => {
        this.timeout = null;
        if (this.isConnected && this.drawerIsOpen() && this.input.value.trim() === searchTerm) {
          this.getSearchResults(searchTerm);
        }
      }, 300);
    }

    handleKeyboardNavigation(event) {
      if (event.isComposing || this.isComposing) return;
      if (event.key === 'Escape') {
        if (this.input.value || this.isOpen || this.timeout !== null || this.abortController) {
          event.preventDefault();
          event.stopPropagation();
          this.close({ clear: true, focusInput: true });
        } else if (this.searchDrawer && this.drawerIsOpen()) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
          this.pendingDismissal = true;
          this.searchDrawer.toggleDrawer();
        }
        return;
      }
      if (!this.isOpen || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
      const focusableResults = Array.from(
        this.resultsContainer.querySelectorAll('a[href], button:not([disabled])')
      ).filter(element => element.getClientRects().length > 0);
      if (!focusableResults.length) return;
      event.preventDefault();
      const currentIndex = focusableResults.indexOf(document.activeElement);
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = currentIndex === -1
        ? (direction === 1 ? 0 : focusableResults.length - 1)
        : (currentIndex + direction + focusableResults.length) % focusableResults.length;
      focusableResults[nextIndex].focus();
    }

    setDrawerSearchState(hasResults) {
      const compactViewport = typeof DeviceDetector !== 'undefined' &&
        (DeviceDetector.isMobile() || DeviceDetector.isTablet());
      if (this.searchDrawerContent) {
        this.searchDrawerContent.style.height = hasResults && compactViewport ? '100%' : this.initialDrawerHeight;
      }
      if (this.searchSuggestions) {
        this.searchSuggestions.style.display = hasResults ? 'none' : this.initialSuggestionsDisplay;
      }
    }

    requestIsCurrent(controller, sequence, searchTerm) {
      return this.isConnected && this.initialized && this.drawerIsOpen() &&
        this.abortController === controller && this.requestSequence === sequence &&
        !this.isComposing && this.input.value.trim() === searchTerm;
    }

    async getSearchResults(searchTerm) {
      if (!this.resultsContainer) return;
      this.cancelPending();
      const controller = new AbortController();
      const sequence = this.requestSequence;
      this.abortController = controller;
      this.resultsContainer.setAttribute('aria-busy', 'true');
      this.setDrawerSearchState(true);
      const params = new URLSearchParams({
        q: normalizeSearch(searchTerm),
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': PRODUCT_SEARCH_FIELDS.join(','),
        section_id: 'predictive-search'
      });

      try {
        const endpoint = window.routes?.predictive_search_url || `${window.Shopify?.routes?.root || '/'}search/suggest`;
        const response = await fetch(`${endpoint}?${params.toString()}`, { signal: controller.signal });
        if (!this.requestIsCurrent(controller, sequence, searchTerm)) return;
        if (!response.ok) throw new Error('Predictive search unavailable');
        const text = await response.text();
        // A response body can finish after a newer request, clear, Escape or
        // section replacement, even when aborting the original fetch was late.
        if (!this.requestIsCurrent(controller, sequence, searchTerm)) return;
        const resultsSection = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!resultsSection) throw new Error('Invalid predictive search response');
        resultsSection.querySelector('.predictive-search__results')?.removeAttribute('id');
        this.resultsContainer.innerHTML = resultsSection.innerHTML;
        // Product cards own their purchase forms outside the search form. Only
        // the explicit View results control submits the customer's search.
        this.resultsContainer.querySelectorAll('button[type="submit"].predictive-search__link')
          .forEach(button => button.setAttribute('form', this.form.id));
        this.open();
        document.querySelector('quick-cart-drawer')?.initTriggers();
      } catch (error) {
        if (error.name === 'AbortError' || !this.requestIsCurrent(controller, sequence, searchTerm)) return;
        // Keep the translated error message already rendered by Liquid.
        this.errorMessageContainer?.classList.remove('hidden');
        this.setDrawerSearchState(false);
      } finally {
        if (this.abortController === controller) {
          this.abortController = null;
          this.resultsContainer?.removeAttribute('aria-busy');
        }
      }
    }

    open() {
      this.isOpen = true;
      this.classList.add('is-open');
      this.resultsContainer?.classList.remove('hidden');
      this.errorMessageContainer?.classList.add('hidden');
      this.updateClearButton();
    }

    close({ clear = false, focusInput = false, restoreDrawer = true } = {}) {
      this.cancelPending();
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer?.classList.add('hidden');
      this.errorMessageContainer?.classList.add('hidden');
      if (clear && this.input) this.input.value = '';
      this.updateClearButton();
      if (restoreDrawer) this.setDrawerSearchState(false);
      if (focusInput && this.isConnected) this.input?.focus();
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
